import { brand } from "../../config/theme";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import {
  AppsSidebar,
  useFilteredCategories,
} from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import Swal from "sweetalert2";
import { faPlug } from "@fortawesome/free-solid-svg-icons";

import IntegradoresMonitor from "./components/IntegradoresMonitor";
import type { IntegradoresMode } from "./types";

interface Props {
  mode: IntegradoresMode;
}

export default function IntegradoresLayout({ mode }: Props) {
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);

  const companyId = useCompaniesStore((state) => state.companyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const companies = useCompaniesStore((state) => state.companies);
  const fetchAllCompanies = useCompaniesStore(
    (state) => state.fetchAllCompanies
  );
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);

  const isReseller =
    (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  const appId = mode === "internal" ? "integradores" : "fleet-integradores";

  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      appId,
      t("apps.integradores.name"),
      t("apps.integradores.description"),
      faPlug,
      "bg-cyan-600"
    );
  }, [setActiveApp, t, appId]);

  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    } else if (!storedCompanyId && storedCompanyUser && !isReseller) {
      setCompanyId(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReseller && allCompanies.length === 0) {
      fetchAllCompanies();
    }
  }, [isReseller, allCompanies.length, fetchAllCompanies]);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === appId) {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${
        isDarkMode ? "bg-dark-bg" : "bg-gray-50"
      }`}
    >
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <Header
        onOpenAppsSidebar={openAppsSidebar}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={isReseller ? allCompanies : companies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
        hideCompanySelector={mode === "internal"}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <IntegradoresMonitor mode={mode} />
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={appId}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
